import { V2_ENDPOINT } from "@/consts";
import { useDefinitionByType } from "@/hooks/useActionTriggerDefinitions";
import { useB3Profile } from "@/hooks/useB3Profile";
import { useFixWithAI } from "@/hooks/useFixWithAI";
import { useNodeExecutionStatus } from "@/hooks/useNodeExecutionStatus";
import { useHyperliquidPositions, useHyperliquidSpotBalance } from "@/hooks/useHyperliquidAccount";
import { HL_GAS_BADGE_POLL_INTERVAL } from "@/lib/hyperliquid/constants";
import { getNodeGasRequirements, computeNodeGasStatus } from "@/hooks/useNodeGasStatus";
import { useResolvedNodeVisual } from "@/hooks/useResolvedNodeVisual";
import { useErc4626Asset } from "@/hooks/useErc4626Asset";
import { containsTemplateVariable } from "@/components/ui/workflow-step-row/components/VariablePill";
import { useSimBalance, useSimTokenInfo } from "@/hooks/useSimTokenInfo";
import { useX402EndpointById, x402EndpointToAction } from "@/hooks/useX402Endpoints";
import { useRelayTokenSearch } from "@/hooks/useRelayTokenSearch";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import { SOLANA_CHAIN_IDS } from "@/lib/chains";
import { isSolanaAddress } from "@/lib/utils/address";
import type { WorkflowDefinition } from "@/stores/workflowsStore";
import { POLYGON_PUSD_ADDRESS_LOWER } from "@/lib/polymarket-constants";
import { ACCOUNT_TYPE_POLYMARKET_DEPOSIT_WALLET } from "@/lib/constants/wallet";
import { isNativeTokenAddress } from "@/lib/tokens/nativeToken";
import { useConnectorsStore } from "@/stores/connectorsStore";
import { useNodeEditorStore } from "@/stores/nodeEditorStore";
import { useRunExecutionStore } from "@/stores/runExecutionStore";
import { useWorkflowRunsStore } from "@/stores/workflowRunsStore";
import type { WorkflowComment } from "@/types/workflow-comments";
import { getRequiredConnectorType } from "@/utils/workflowTransform";
import { useQuery } from "@tanstack/react-query";
import { useReactFlow } from "@xyflow/react";
import isEqual from "fast-deep-equal";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";
// Performance optimization: Use TanStack Query for chain info caching
import NodeSelectionIndicator from "@/components/live/NodeSelectionIndicator";
import { useAIHighlight } from "../_hooks/useAIHighlight";
import { useChainInfoMap } from "../_hooks/useChainInfo";
import { useOrgWalletsContext } from "./OrganizationWalletsContext";
import { useCanvasGasData } from "./CanvasGasDataContext";
import { useCompactView } from "./CompactViewContext";
import { useDescriptionFormatHints } from "@/hooks/useDescriptionFormatHints";
import { resolveDescriptionTemplates, NATIVE_ONLY_ACTION_TYPES } from "@/utils/resolveDescriptionTemplates";
import { NodeDescriptionToolbar } from "./NodeDescriptionToolbar";
import { NodeGasWarningToolbar } from "./NodeGasWarningToolbar";
import { useNodeDescriptionVisible, useNodeHoverSetters } from "./NodeHoverContext";
import { useMultipleNodesSelected } from "./useMultipleNodesSelected";
import { useActionTokenFormatting } from "./useActionTokenFormatting";
import { NodeValidationErrorToolbar } from "./NodeValidationErrorToolbar";
import { getRelativeTime } from "@/lib/utils/formatRelativeTime";
import { useNodeValidationErrors } from "./ValidationErrorContext";
import NodeCommentsBadge from "./NodeCommentsBadge";
import NodeExecutionDetails, { type NodeExecutionDetailsHandle } from "./NodeExecutionDetails";

// Split-out sub-modules
import { isMathOperationPayload, type TokenInfo } from "./ActionNodeValueFormat";
import { NodeSubtitle, ScheduledBanner, PolymarketBanner, NodeBodyContent } from "./ActionNodeStatus";
import { PolymarketFundingModal } from "./PolymarketFundingModal";
import { PolymarketDepositWalletSetupModal } from "./PolymarketDepositWalletSetupModal";
import { ActionNodeShell } from "./ActionNodeShell";
import { useSlackChannels } from "../_hooks/useSlackChannels";
import { listTelegramChats, type TelegramChat } from "@/lib/telegramBot";
import { fetchQBAccounts, type QBAccount } from "./form-fields/quickbooksAccounts";
import { ContextMenu, ContextMenuTrigger, ActionNodeContextMenuContent, ActionNodeModals } from "./ActionNodeContextMenu";
import { NOTIFICATION_NODE_TYPE } from "@/lib/workflow/notificationChannels";
interface ActionNodeProps {
  data: {
    name?: string;
    type?: string;
    description?: string;
    platform?: string;
    payload?: Record<string, unknown>;
    connector?: unknown;
    titleOverride?: string;
    isTemplateView?: boolean;
    horizontalHandles?: boolean;
    hideSourceHandle?: boolean;
    onAddComment?: (nodeId: string) => void;
    onAddNodeComment?: (text: string) => void;
    nodeComments?: WorkflowComment[];
    onDeselectNode?: () => void;
    onUpdateNode?: (nodeId: string, data: Record<string, unknown>) => void;
    onRollback?: (targetVersion: number) => void;
    apiNodeId?: string;
    parentForEachApiNodeId?: string;
    isPublicReadOnly?: boolean;
    isStreamingNew?: boolean;
    isStreamingEdit?: boolean;
    isStreamingActive?: boolean;
  };
  selected?: boolean;
  id: string;
}

// Module-scope so the array reference stays stable across renders. The inline
// Memoized for performance - React Flow re-renders nodes frequently
function ActionNodeComponent({
  data,
  selected,
  id
}: ActionNodeProps) {
  const {
    deleteElements,
    getNode,
    setNodes
  } = useReactFlow();
  const isCompactView = useCompactView();

  // Resolve React Flow node ID -> display name for variable pills
  const getNodeName = useCallback((nodeId: string): string | undefined => {
    const node = getNode(nodeId);
    return (node?.data as Record<string, unknown>)?.name as string | undefined;
  }, [getNode]);

  // Performance optimization: Use TanStack Query for chain info caching
  const chainIdsToFetch = useMemo(() => {
    if (!data.payload) return [];
    const ids: number[] = [];
    Object.entries(data.payload).forEach(([key, value]) => {
      if ((key.toLowerCase().includes("chainid") || key.toLowerCase().includes("chain_id")) && typeof value === "number") {
        ids.push(value);
      }
      if (key === "chainIds" && Array.isArray(value)) {
        value.forEach(id_0 => {
          if (typeof id_0 === "number") {
            ids.push(id_0);
          }
        });
      }
    });
    return ids;
  }, [data.payload]);
  const {
    chainInfoMap
  } = useChainInfoMap(chainIdsToFetch);
  const [contractNameMap, setContractNameMap] = useState<Map<string, string>>(new Map());
  const fetchedContractAddresses = useRef(new Set<string>());
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const nodeRootRef = useRef<HTMLDivElement>(null);
  const nodeExecutionDetailsRef = useRef<NodeExecutionDetailsHandle>(null);

  // Track hover state globally for description toolbar visibility
  // PERFORMANCE: Use setters-only hook to avoid re-renders when other nodes are hovered
  const {
    setHoveredNodeId,
    setSelectedNodeId
  } = useNodeHoverSetters();
  const {
    showToolbar: showDescriptionToolbar,
    setToolbarHovered,
    canShowSelectedToolbar
  } = useNodeDescriptionVisible(id, isHovered, !!selected);
  // Read directly from React Flow store — same render cycle as `selected`, no 1-frame flash
  const multipleNodesSelected = useMultipleNodesSelected();

  // Get validation errors for this node (use API node ID since server returns errors with API IDs)
  const validationNodeId = (data as {
    apiNodeId?: string;
  }).apiNodeId || id;
  const validationErrors = useNodeValidationErrors(validationNodeId);
  const isBeingConfigured = useNodeEditorStore(s => s.newlyAddedNodeId) === id;
  const hasValidationErrors = validationErrors.length > 0 && !isBeingConfigured;

  // Update selected node in context when selection changes
  useEffect(() => {
    if (selected) {
      setSelectedNodeId(id);
    } else {
      // Clear selection if this node was deselected
      setSelectedNodeId(null);
      // Clear newly-added flag when user navigates away from this node
      if (useNodeEditorStore.getState().newlyAddedNodeId === id) {
        useNodeEditorStore.getState().setNewlyAddedNodeId(null);
      }
    }
  }, [selected, id, setSelectedNodeId]);

  // "Fix with AI" functionality for failed workflow runs
  const handleFixWithAI = useFixWithAI(data.onDeselectNode);

  // Apply a field-level fix from AI error analysis to the node payload.
  // Supports dot-notation for nested fields (e.g. "headers.Authorization").
  // Uses onUpdateNode (from WorkflowCanvas → handleUpdateNode) which syncs selectedNode, undo, and autosave.
  const handleFieldUpdate = useCallback((fieldName: string, value_0: unknown) => {
    const applyNestedUpdate = (obj: Record<string, unknown>, path: string[], val: unknown): Record<string, unknown> => {
      const [key_0, ...rest] = path;
      if (!key_0) return obj;
      if (rest.length === 0) return {
        ...obj,
        [key_0]: val
      };
      const child = typeof obj[key_0] === "object" && obj[key_0] !== null && !Array.isArray(obj[key_0]) ? obj[key_0] as Record<string, unknown> : {};
      return {
        ...obj,
        [key_0]: applyNestedUpdate(child, rest, val)
      };
    };
    const path_0 = fieldName.split(".");
    if (data.onUpdateNode) {
      const payload = data.payload as Record<string, unknown> || {};
      data.onUpdateNode(id, {
        ...data,
        payload: applyNestedUpdate(payload, path_0, value_0)
      });
    } else {
      // Fallback: direct React Flow setNodes (won't sync sidebar selectedNode)
      setNodes(nds => nds.map(n => {
        if (n.id !== id) return n;
        const payload_0 = n.data.payload as Record<string, unknown> || {};
        return {
          ...n,
          data: {
            ...n.data,
            payload: applyNestedUpdate(payload_0, path_0, value_0)
          }
        };
      }));
    }
  }, [id, data, setNodes]);

  // Track AI update highlight effect
  const showAIHighlight = useAIHighlight((data as any)._refreshKey);

  // Get the action definition to check if it requires a connector
  const definition = useDefinitionByType(data.type as string);
  const iconVisual = useResolvedNodeVisual(data.type);
  const isNotificationNode = data.type === NOTIFICATION_NODE_TYPE;
  const notificationMessage = isNotificationNode && typeof data.payload?.message === "string" ? data.payload.message : undefined;

  // Get chainId from payload for contract name lookups
  const payloadChainId = data.payload?.chainId as number | undefined;
  const tokenAddress = data.payload?.tokenAddress as string | undefined;
  const recipientAddress = data.payload?.recipientAddress as string | undefined;
  const tokenInAddress = data.payload?.tokenIn as string | undefined;
  const tokenOutAddress = data.payload?.tokenOut as string | undefined;
  const sellTokenAddress = data.payload?.sellToken as string | undefined;
  const buyTokenAddress = data.payload?.buyToken as string | undefined;
  const vaultAddress = typeof data.payload?.vaultAddress === "string" ? data.payload.vaultAddress : undefined;
  const srcChainId = data.payload?.srcChainId as number | undefined;
  const dstChainId = data.payload?.dstChainId as number | undefined;
  const connectorId = (data.connector as {
    id?: string;
  } | undefined)?.id;
  const connectorType = (data.connector as {
    type?: string;
  } | undefined)?.type;

  // Check if this is a native-only action (always uses 18 decimals + chain gas token)
  const isNativeOnlyAction = NATIVE_ONLY_ACTION_TYPES.includes(data.type as (typeof NATIVE_ONLY_ACTION_TYPES)[number]);

  // Check if this is a send ERC20 token action
  const isSendErc20Token = data.type === "send-erc20-token";

  // Fetch recipient profile for send-erc20-token
  const {
    data: recipientProfile
  } = useB3Profile({
    addressOrName: recipientAddress,
    enabled: isSendErc20Token && !!recipientAddress
  });

  // Check if this is a DEX swap action
  const isDexSwap = data.type === "v2-swap";

  // Check if this is a Relay cross-chain swap action
  const isRelaySwap = data.type === "relay-swap";

  // Check if this is a math operation
  const isMathOperation = isMathOperationPayload(data.payload);

  // Check if this is a trigger-workflow action
  const isTriggerWorkflow = data.type === "trigger-workflow";
  const targetWorkflowId = isTriggerWorkflow ? data.payload?.workflowId as string | undefined : undefined;

  // Fetch target workflow details for trigger-workflow nodes
  const {
    data: targetWorkflow
  } = useQuery({
    queryKey: ["workflow-preview", targetWorkflowId],
    queryFn: async () => {
      const res = await authenticatedFetch(`${V2_ENDPOINT}/v1/workflows/${targetWorkflowId}`);
      if (!res.ok) throw new Error("Failed to fetch workflow");
      const json = await res.json();
      return json.data as {
        id: string;
        name: string;
        definition: WorkflowDefinition;
      };
    },
    enabled: !!targetWorkflowId && !data.isPublicReadOnly,
    staleTime: 5 * 60 * 1000
  });

  // Check if chainIds array is present (to hide single chainId)
  const hasChainIdsArray = data.payload?.chainIds && Array.isArray(data.payload.chainIds) && data.payload.chainIds.length > 0;

  // Filter out empty/undefined payload values and hide abi fields
  const isPolymarketAction = data.type === "polymarket-place-bet";
  const isPolymarketCopyMode = isPolymarketAction && data.payload?.mode === "copy";
  const isPolymarketManualMode = isPolymarketAction && data.payload?.mode !== "copy";
  const needsDepositWallet = definition?.connector?.accountType === ACCOUNT_TYPE_POLYMARKET_DEPOSIT_WALLET;

  // PERF: Read org wallets from canvas-level context (single subscription) instead of per-node hook
  const {
    wallets: polymarketOrgWallets,
    isLoading: walletsLoading
  } = useOrgWalletsContext();
  const polymarketWallet = useMemo(() => {
    if (!needsDepositWallet || polymarketOrgWallets.length === 0) return undefined;
    const connectorId_0 = (data.connector as {
      id?: string;
    })?.id;
    if (connectorId_0) {
      const wallet = polymarketOrgWallets.find(w => w.id === connectorId_0);
      if (wallet?.accountType === ACCOUNT_TYPE_POLYMARKET_DEPOSIT_WALLET) return wallet;
    }
    const depositWallets = polymarketOrgWallets.filter(w_0 => w_0.accountType === ACCOUNT_TYPE_POLYMARKET_DEPOSIT_WALLET);
    return depositWallets.find(w_1 => w_1.isDefault) || depositWallets[0];
  }, [needsDepositWallet, data.connector, polymarketOrgWallets]);
  const polymarketWalletAddress = polymarketWallet?.address;
  const canvasGasData = useCanvasGasData();
  const {
    data: polygonBalanceData
  } = useSimBalance(polymarketWalletAddress, [137], needsDepositWallet);
  const polymarketHasPusd = useMemo(() => {
    if (!polygonBalanceData?.balances) return false;
    return polygonBalanceData.balances.some(b => b.address.toLowerCase() === POLYGON_PUSD_ADDRESS_LOWER && parseFloat(b.amount) > 0);
  }, [polygonBalanceData]);
  const hasDepositWallet = polymarketWallet?.accountType === ACCOUNT_TYPE_POLYMARKET_DEPOSIT_WALLET;
  const polymarketFundingRecipient = hasDepositWallet ? polymarketWalletAddress : undefined;
  const [showPolymarketFundingModal, setShowPolymarketFundingModal] = useState(false);
  const [showPolymarketSetupModal, setShowPolymarketSetupModal] = useState(false);

  // Compute the effective "amount" for Polymarket actions
  const polymarketAmount = isPolymarketAction ? isPolymarketCopyMode ? data.payload?.dynamicAmount // Copy mode uses dynamic amount
  : data.payload?.fixedAmount // Manual mode uses fixed amount
  : null;

  // Filter out empty/undefined payload values and hide internal fields
  const configuredValues = useMemo(() => {
    const filtered = Object.entries(data.payload || {}).filter(([key_1, value_1]) => {
      // Hide abi fields since we show functionName/eventName instead
      if (key_1 === "abi" || key_1 === "eventAbi") return false;
      // Hide single chainId if chainIds array is present
      if (key_1 === "chainId" && hasChainIdsArray) return false;

      // For Polymarket, hide the separate amount fields - we'll show a unified "amount" instead
      if (isPolymarketAction) {
        const alwaysHidden = ["fixedAmount", "dynamicAmount", "amountMode"];
        if (alwaysHidden.includes(key_1)) return false;
      }

      // Hide irrelevant fields in Polymarket copy mode
      if (isPolymarketCopyMode) {
        // These fields are not used in copy mode (dynamic values come from trigger)
        const hiddenInCopyMode = ["side", "orderType", "marketUrl", "outcome", "copyPriceVariable", "priceMode"];
        if (hiddenInCopyMode.includes(key_1)) return false;
      }
      // Hide copy mode fields in manual mode
      if (isPolymarketManualMode) {
        const hiddenInManualMode = ["tokenIdVariable", "sideVariable", "multiplier", "minAmount", "maxAmount"];
        if (hiddenInManualMode.includes(key_1)) return false;
      }
      // Hide workflowId for trigger-workflow (shown as workflow name in special display)
      if (key_1 === "workflowId" && isTriggerWorkflow) return false;
      // Hide internal x402 fields (endpointId and _prefixed display fields)
      if (key_1 === "endpointId" || key_1.startsWith("_endpoint")) return false;
      // Hide scheduledAt from payload preview (shown as a dedicated banner)
      if (key_1 === "scheduledAt") return false;
      if (value_1 === null || value_1 === undefined || value_1 === "") return false;
      if (Array.isArray(value_1) && value_1.length === 0) return false;
      return true;
    });

    // Add computed amount field for Polymarket
    if (isPolymarketAction && polymarketAmount) {
      return [...filtered, ["amount", polymarketAmount] as [string, unknown]];
    }
    return filtered;
  }, [data.payload, hasChainIdsArray, isTriggerWorkflow, isPolymarketAction, isPolymarketCopyMode, isPolymarketManualMode, polymarketAmount]);
  const hasConfiguredValues = configuredValues.length > 0;
  const hasSlackFields = !data.isPublicReadOnly && (typeof data.payload?.conversation === "string" || typeof data.payload?.channel === "string" || Array.isArray(data.payload?.conversations) && data.payload.conversations.length > 0);
  const {
    slackChannelsMap,
    isSlackConnector
  } = useSlackChannels(connectorId, connectorType, hasSlackFields);
  const shouldFetchTelegramChats = !!connectorId && connectorType === "telegram-bot" && !data.isPublicReadOnly && (typeof data.payload?.chatId === "string" || typeof data.payload?.chatId === "number");
  const {
    data: telegramChats = []
  } = useQuery<TelegramChat[]>({
    queryKey: ["telegram-chats", connectorId],
    queryFn: async () => (await listTelegramChats(undefined, 50, 0, connectorId!)).items,
    enabled: shouldFetchTelegramChats,
    staleTime: 5 * 60 * 1000
  });
  const telegramChatsMap = useMemo(() => new Map(telegramChats.map(chat => [String(chat.chatId), chat])), [telegramChats]);
  const shouldFetchQuickBooksAccounts = !!connectorId && (connectorType === "pipedream:quickbooks" || connectorType === "pipedream:quickbooks_sandbox") && !data.isPublicReadOnly && (typeof data.payload?.accountId === "string" || typeof data.payload?.expenseAccountId === "string");
  const {
    data: quickBooksAccounts = []
  } = useQuery<QBAccount[]>({
    queryKey: ["quickbooks-accounts", connectorId, connectorType],
    queryFn: () => fetchQBAccounts(connectorId!, connectorType!),
    enabled: shouldFetchQuickBooksAccounts,
    staleTime: 5 * 60 * 1000
  });
  const quickBooksAccountsMap = useMemo(() => new Map(quickBooksAccounts.map(account => [account.Id, account])), [quickBooksAccounts]);

  // Extract x402 display info from payload (hidden fields with _ prefix)
  const isX402Endpoint = data.type === "call-x402-endpoint";
  const endpointId = isX402Endpoint ? data.payload?.endpointId as string | undefined : undefined;

  // Always fetch endpoint data for x402 nodes - needed for info modal even if we have cached display data
  const {
    endpoint: fetchedEndpoint
  } = useX402EndpointById(isX402Endpoint ? endpointId : null);

  // Merge fetched endpoint data with payload data (payload takes precedence)
  const x402Info = isX402Endpoint ? {
    name: data.payload?._endpointName as string | undefined || fetchedEndpoint?.name,
    description: data.payload?._endpointDescription as string | undefined || fetchedEndpoint?.description,
    url: data.payload?._endpointUrl as string | undefined || fetchedEndpoint?.baseUrl,
    price: data.payload?._endpointPrice as string | undefined || fetchedEndpoint?.price,
    network: data.payload?._endpointNetwork as string | undefined || fetchedEndpoint?.network,
    tokenSymbol: data.payload?._endpointTokenSymbol as string || fetchedEndpoint?.tokenSymbol || "USDC",
    tokenDecimals: data.payload?._endpointTokenDecimals as number || fetchedEndpoint?.tokenDecimals || 6
  } : null;

  // Build the definition for the info modal - for x402 nodes, use endpoint-specific data
  const modalDefinition = useMemo(() => {
    if (isX402Endpoint && fetchedEndpoint) {
      const actionDef = x402EndpointToAction(fetchedEndpoint);
      return {
        ...actionDef,
        _isX402: true,
        _endpoint: fetchedEndpoint
      };
    }
    return definition;
  }, [isX402Endpoint, fetchedEndpoint, definition]);

  // PERF: useShallow prevents all N nodes re-rendering when connectorTypes array ref changes
  const connectorTypes = useConnectorsStore(useShallow(state => state.connectorTypes));

  // Check if connector is required but missing
  // Use getRequiredConnectorType to handle dynamic node types (e.g., x402 endpoints) that don't have definitions
  const requiredConnectorType = getRequiredConnectorType(data.type, definition);
  const connectorRequired = !!requiredConnectorType;
  // Connector is "missing" if it's null/undefined OR if it's incomplete (has type but no id)
  const connectorMissing = data.connector === null || data.connector === undefined || data.connector && !(data.connector as {
    id?: string;
  }).id;
  // Check if the connector type is marked as optional
  const connectorTypeDefinition = requiredConnectorType ? connectorTypes.find(ct => ct.type === requiredConnectorType) : undefined;
  const isOptionalConnector = connectorTypeDefinition?.optional === true;
  // Don't show warning if connector types haven't loaded yet (can't determine if optional)
  const connectorTypesLoaded = connectorTypes.length > 0;
  // Only show "connector required" if connector is required, missing, NOT optional, AND types are loaded
  const needsConnector = connectorRequired && connectorMissing && !isOptionalConnector && connectorTypesLoaded;

  // PERF: Pass pre-fetched wallets from context to avoid per-node useOrganizationWallets subscription
  const {
    wallets: orgWallets,
    isLoading: isLoadingOrgWallets
  } = useOrgWalletsContext();
  const gasEnabled = !data.isTemplateView && !needsConnector && connectorTypesLoaded;
  const gasReqs = useMemo(() => getNodeGasRequirements({
    type: data.type,
    payload: data.payload,
    connector: data.connector as {
      id?: string;
      walletAddress?: string;
    } | undefined
  }, definition, orgWallets), [data.type, data.payload, data.connector, definition, orgWallets]);
  const hasRequiredTokens = !!gasReqs.requiredTokens && gasReqs.requiredTokens.length > 0;
  const shouldFetchBalance = gasEnabled && (gasReqs.requiresGas || hasRequiredTokens) && !!gasReqs.walletAddress && !!gasReqs.fetchChainIds;
  const {
    data: gasBalanceData,
    isLoading: isLoadingGasBalance
  } = useSimBalance(gasReqs.walletAddress, gasReqs.fetchChainIds, shouldFetchBalance);
  const autoSwap = canvasGasData.autoSwapCoverage;
  const hlEnabled = gasEnabled && gasReqs.needsHlBalance && !!gasReqs.walletAddress;
  // Use the slower gas-badge poll interval — HL balance shown on a node pill
  // doesn't need real-time freshness; live-trading surfaces (positions,
  // wallet management) keep the default 5s cadence.
  const {
    data: hlState,
    isLoading: isLoadingHlPerps
  } = useHyperliquidPositions(gasReqs.walletAddress, hlEnabled, HL_GAS_BADGE_POLL_INTERVAL);
  const {
    data: hlSpot,
    isLoading: isLoadingHlSpot
  } = useHyperliquidSpotBalance(gasReqs.walletAddress, hlEnabled, HL_GAS_BADGE_POLL_INTERVAL);
  const isLoadingHl = isLoadingHlPerps || isLoadingHlSpot;
  const gasStatus = useMemo(() => {
    return computeNodeGasStatus(gasReqs, {
      enabled: gasEnabled,
      isLoadingWallets: isLoadingOrgWallets,
      balanceData: gasBalanceData,
      isLoadingBalance: isLoadingGasBalance,
      autoSwapCoverage: autoSwap,
      hlState,
      hlSpot,
      isLoadingHl
    });
  }, [gasReqs, gasEnabled, isLoadingOrgWallets, gasBalanceData, isLoadingGasBalance, autoSwap, hlState, hlSpot, isLoadingHl]);
  const hasBalanceWarning = gasStatus.needsGasWarning || gasStatus.needsTokenWarning;

  // PERF: Memoize connector prop to avoid new object on every render (defeats React.memo in children)
  const connectorLogoUrl = connectorTypeDefinition?.logoUrl;
  const connectorProp = useMemo(() => requiredConnectorType ? {
    type: requiredConnectorType,
    logoUrl: connectorLogoUrl
  } : connectorType ? {
    type: connectorType
  } : undefined, [requiredConnectorType, connectorLogoUrl, connectorType]);

  // Get execution status for this node using API node ID (server sends events with API IDs)
  const executionNodeId = data.apiNodeId || id;
  const parentForEachApiNodeId = data.parentForEachApiNodeId;
  const {
    status: executionStatus,
    hasActiveRun,
    nodeState,
    runId
  } = useNodeExecutionStatus(executionNodeId, parentForEachApiNodeId);

  // Get current workflow ID for error modals (prefer run execution store, fallback to runs store)
  const runWorkflowId = useRunExecutionStore(state_0 => state_0.currentRun?.workflowId);
  const runsStoreWorkflowId = useWorkflowRunsStore(state_1 => state_1.currentWorkflowId);
  const currentWorkflowId = runWorkflowId || runsStoreWorkflowId;

  // Live countdown for scheduledAt banner (refreshes every 60s).
  // Falls back to execution state for template expressions (e.g., "{{root.result.sendAt}}")
  // where the payload value is an unresolved template but the backend resolved it.
  const payloadScheduledAt = data.payload?.scheduledAt as string | undefined;
  const executionScheduledAt = nodeState?.scheduledAt;
  const effectiveScheduledAt = useMemo(() => {
    if (payloadScheduledAt && !isNaN(new Date(payloadScheduledAt).getTime())) {
      return payloadScheduledAt;
    }
    return executionScheduledAt;
  }, [payloadScheduledAt, executionScheduledAt]);
  const [scheduledRelativeTime, setScheduledRelativeTime] = useState<string>("");
  useEffect(() => {
    if (!effectiveScheduledAt) {
      setScheduledRelativeTime("");
      return;
    }
    const update = () => {
      const d = new Date(effectiveScheduledAt);
      if (!isNaN(d.getTime())) {
        setScheduledRelativeTime(getRelativeTime(d));
      }
    };
    update();
    const interval = setInterval(update, 1_000);
    return () => clearInterval(interval);
  }, [effectiveScheduledAt]);

  // Determine if we should show output preview instead of settings
  // Show output when execution is complete (success or failure) and we have results
  const showOutputPreview = hasActiveRun && (executionStatus === "success" || executionStatus === "failure") && nodeState?.result && Object.keys(nodeState.result).length > 0;

  // Note: Chain info is now fetched via useChainInfoMap hook at top (TanStack Query)

  // Get first chainId from chainIds array for token lookup (if no single chainId)
  const firstChainIdFromArray = hasChainIdsArray && Array.isArray(data.payload?.chainIds) ? data.payload.chainIds[0] as number : undefined;
  const chainIdForTokenLookup = payloadChainId || firstChainIdFromArray;

  // For relay-swap, tokenIn uses srcChainId and tokenOut uses dstChainId
  const tokenInChainId = isRelaySwap ? srcChainId : chainIdForTokenLookup;
  const tokenOutChainId = isRelaySwap ? dstChainId : chainIdForTokenLookup;

  // Resolve underlying token for ERC-4626 vaults — use schema annotation, not structural heuristic
  const hasErc4626Annotation = Object.values(definition?.payloadSchema?.properties ?? {}).some((prop: any) => prop["x-token-resolve"] === "erc4626");
  const needsVaultResolution = hasErc4626Annotation && !!vaultAddress && !containsTemplateVariable(vaultAddress) && !!chainIdForTokenLookup;
  const {
    data: vaultUnderlyingAsset
  } = useErc4626Asset(vaultAddress, chainIdForTokenLookup, needsVaultResolution);
  const effectiveTokenAddress = tokenAddress || vaultUnderlyingAsset;

  // Fetch token info for any action with tokenAddress + chainId (or chainIds)
  const shouldFetchTokenInfo = !!(effectiveTokenAddress && chainIdForTokenLookup && effectiveTokenAddress.startsWith("0x"));
  const {
    data: simTokenData
  } = useSimTokenInfo(effectiveTokenAddress, chainIdForTokenLookup, shouldFetchTokenInfo);

  // Fetch token info for tokenIn and tokenOut (e.g., DEX swap, relay swap actions)
  const shouldFetchTokenIn = !!(tokenInAddress && tokenInChainId && tokenInAddress.startsWith("0x"));
  const shouldFetchTokenOut = !!(tokenOutAddress && tokenOutChainId && tokenOutAddress.startsWith("0x"));
  const {
    data: simTokenInData
  } = useSimTokenInfo(tokenInAddress, tokenInChainId, shouldFetchTokenIn);
  const {
    data: simTokenOutData
  } = useSimTokenInfo(tokenOutAddress, tokenOutChainId, shouldFetchTokenOut);

  // Fetch token info for sellToken/buyToken (e.g., 0x-swap, cow-swap actions)
  const shouldFetchSellToken = !!(sellTokenAddress && chainIdForTokenLookup && sellTokenAddress.startsWith("0x"));
  const shouldFetchBuyToken = !!(buyTokenAddress && chainIdForTokenLookup && buyTokenAddress.startsWith("0x"));
  const {
    data: simSellTokenData
  } = useSimTokenInfo(sellTokenAddress, chainIdForTokenLookup, shouldFetchSellToken);
  const {
    data: simBuyTokenData
  } = useSimTokenInfo(buyTokenAddress, chainIdForTokenLookup, shouldFetchBuyToken);

  // Fetch Solana token metadata via Relay for non-EVM token addresses
  const isSolanaChainForToken = !!(chainIdForTokenLookup && SOLANA_CHAIN_IDS.has(chainIdForTokenLookup));
  const shouldFetchSolanaToken = !!(effectiveTokenAddress && chainIdForTokenLookup && isSolanaChainForToken && isSolanaAddress(effectiveTokenAddress));
  const {
    tokens: solanaTokenResults
  } = useRelayTokenSearch({
    chainId: chainIdForTokenLookup,
    searchTerm: effectiveTokenAddress,
    enabled: shouldFetchSolanaToken,
    limit: 1
  });
  const solanaTokenResult = solanaTokenResults[0] ?? null;

  // Convert SimDune token data to TokenInfo format (EVM), or Relay data (Solana)
  useEffect(() => {
    if (simTokenData && simTokenData.tokens.length > 0) {
      const token = simTokenData.tokens[0];
      // The logo can be in token.logo or token.token_metadata?.logo
      const logoUrl = token.logo || token.token_metadata?.logo;
      setTokenInfo({
        chainId: token.chain_id,
        address: simTokenData.contract_address,
        symbol: token.symbol,
        name: token.name || token.symbol,
        decimals: token.decimals,
        metadata: {
          logoURI: logoUrl,
          verified: true,
          isNative: isNativeTokenAddress(simTokenData.contract_address)
        }
      });
    } else if (solanaTokenResult) {
      setTokenInfo({
        chainId: solanaTokenResult.chainId,
        address: solanaTokenResult.address,
        symbol: solanaTokenResult.symbol,
        name: solanaTokenResult.name || solanaTokenResult.symbol,
        decimals: solanaTokenResult.decimals,
        metadata: {
          logoURI: solanaTokenResult.metadata?.logoURI,
          verified: solanaTokenResult.metadata?.verified ?? false,
          isNative: solanaTokenResult.metadata?.isNative ?? false
        }
      });
    } else {
      setTokenInfo(null);
    }
  }, [simTokenData, solanaTokenResult]);
  const {
    inputAmountDecimals,
    resultDecimals,
    vaultShareDecimals
  } = useActionTokenFormatting({
    tokenInfo,
    vaultAddress,
    chainId: chainIdForTokenLookup,
    payload: data.payload,
    hasErc4626Annotation
  });

  // Convert SimDune data to TokenInfo format
  const convertSimTokenToTokenInfo = (simTokenData_0: typeof simTokenInData): TokenInfo | null => {
    if (simTokenData_0 && simTokenData_0.tokens.length > 0) {
      const token_0 = simTokenData_0.tokens[0];
      const logoUrl_0 = token_0.logo || token_0.token_metadata?.logo;
      return {
        chainId: token_0.chain_id,
        address: simTokenData_0.contract_address,
        symbol: token_0.symbol,
        name: token_0.name || token_0.symbol,
        decimals: token_0.decimals,
        metadata: {
          logoURI: logoUrl_0,
          verified: true,
          isNative: isNativeTokenAddress(simTokenData_0.contract_address)
        }
      };
    }
    return null;
  };
  const tokenInInfo = useMemo<TokenInfo | null>(() => convertSimTokenToTokenInfo(simTokenInData), [simTokenInData]);
  const tokenOutInfo = useMemo<TokenInfo | null>(() => convertSimTokenToTokenInfo(simTokenOutData), [simTokenOutData]);
  const sellTokenInfo = useMemo<TokenInfo | null>(() => convertSimTokenToTokenInfo(simSellTokenData), [simSellTokenData]);
  const buyTokenInfo = useMemo<TokenInfo | null>(() => convertSimTokenToTokenInfo(simBuyTokenData), [simBuyTokenData]);
  useEffect(() => {
    if (data.payload && payloadChainId) {
      const addresses: string[] = [];
      Object.entries(data.payload).forEach(([key_2, value_2]) => {
        if ((key_2.toLowerCase().includes("contractaddress") || key_2.toLowerCase().includes("contract_address")) && typeof value_2 === "string" && value_2.startsWith("0x") && value_2.length === 42) {
          addresses.push(value_2.toLowerCase());
        }
      });
      addresses.forEach(address => {
        if (!fetchedContractAddresses.current.has(address)) {
          fetchedContractAddresses.current.add(address);
          fetch(`https://contract-abi-api.b3.fun/list?chainId=${payloadChainId}&limit=100`).then(res_0 => res_0.json()).then(data_0 => {
            const contract = data_0.results?.find((c: any) => c.address.toLowerCase() === address);
            if (contract) {
              setContractNameMap(prev => {
                const newMap = new Map(prev);
                newMap.set(address, contract.name || contract.nameTag);
                return newMap;
              });
            }
          }).catch(err => {
            console.error("Failed to fetch contract name:", err);
          });
        }
      });
    }
  }, [data.payload, payloadChainId]);
  const handleDelete = useCallback(() => {
    // Delay to let ContextMenu fully close before opening Dialog
    // Prevents pointer-events conflicts between the two Radix components
    setTimeout(() => setShowDeleteConfirm(true), 0);
  }, []);
  const confirmDelete = useCallback(() => {
    // Close dialog first, then delete on the next tick. See LogicNode for
    // the full explanation of the Radix DismissableLayer pointer-events race.
    setShowDeleteConfirm(false);
    setTimeout(() => {
      deleteElements({
        nodes: [{
          id
        }]
      });
      requestAnimationFrame(() => {
        if (document.body.style.pointerEvents === "none") {
          document.body.style.removeProperty("pointer-events");
        }
      });
    }, 0);
  }, [deleteElements, id]);
  const handleDuplicate = useCallback(() => {
    const node_0 = getNode(id);
    if (node_0) {
      // TODO: Implement duplicate functionality
      console.log("Duplicate node:", node_0);
    }
  }, [getNode, id]);
  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
    setHoveredNodeId(id);
  }, [id, setHoveredNodeId]);
  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    setHoveredNodeId(null);
  }, [setHoveredNodeId]);

  // Memoize nodeInfo object to prevent unnecessary re-renders
  const nodeInfo = useMemo(() => ({
    name: data.name,
    type: data.type,
    description: data.description,
    category: "action" as const
  }), [data.name, data.type, data.description]);

  // PERF: Memoize tokens array to avoid new object on every render (defeats React.memo in NodeDescriptionToolbar)
  const descriptionTokens = useMemo(() => {
    const tokenEntries = [tokenInfo, tokenInInfo, tokenOutInfo, sellTokenInfo, buyTokenInfo].filter((token_1): token_1 is NonNullable<typeof tokenInfo> => !!token_1).map(token_2 => [token_2.address.toLowerCase(), {
      address: token_2.address,
      symbol: token_2.symbol,
      name: token_2.name,
      chainId: token_2.chainId,
      logoUrl: token_2.metadata.logoURI
    }] as const);
    return tokenEntries.length > 0 ? Array.from(new Map(tokenEntries).values()) : undefined;
  }, [tokenInfo, tokenInInfo, tokenOutInfo, sellTokenInfo, buyTokenInfo]);

  // Format hints for description template resolution (shared hook)
  // Note: buyTokenInfo is intentionally excluded — descriptions reference sell-side amounts
  const descriptionFormatHints = useDescriptionFormatHints({
    simTokenData,
    simTokenInData,
    simSellTokenData,
    tokenInAddress,
    tokenInChainId,
    isNativeOnlyAction,
    chainId: chainIdForTokenLookup
  });

  // Determine if we should show the rotating border effect
  const canShowAnimatedBorder = !showAIHighlight && (!hasActiveRun || executionStatus === "idle") && !needsConnector && !hasBalanceWarning && !hasValidationErrors;
  const showRotatingBorder = selected && canShowAnimatedBorder;
  const showHoverBorder = isHovered && !selected && canShowAnimatedBorder;
  const streamingClass = data.isStreamingNew ? "streaming-enter" : data.isStreamingEdit ? "streaming-edit-highlight" : undefined;
  const isStreamingActive = !!data.isStreamingActive;
  const displayName = isNotificationNode ? resolveDescriptionTemplates(data.titleOverride, data.payload, descriptionFormatHints) || data.name || "Notification" : isX402Endpoint && x402Info?.name || resolveDescriptionTemplates(data.titleOverride, data.payload, descriptionFormatHints) || data.name || data.type || "Action";
  const iconSubject = isX402Endpoint && x402Info?.name || data.name || data.type || "Action";
  const iconContext = isX402Endpoint && x402Info?.description || resolveDescriptionTemplates(data.description, data.payload, descriptionFormatHints) || "";
  const iconAlt = isX402Endpoint && x402Info?.name || data.name || "Action";
  const resolvedConnector = connectorProp ?? iconVisual.connector;
  const subtitleNode = isNotificationNode ? <div className="label-xs text-ds-text-muted truncate">
      {notificationMessage ? "Message configured" : "Configure channel..."}
    </div> : <NodeSubtitle needsConnector={needsConnector} isTemplateView={data.isTemplateView} requiredConnectorType={requiredConnectorType} showOutputPreview={!!showOutputPreview} isX402Endpoint={isX402Endpoint} x402Info={x402Info} executionStatus={executionStatus} nodeState={nodeState} requiresGas={definition?.requiresGas} needsGasWarning={gasStatus.needsGasWarning} needsTokenWarning={gasStatus.needsTokenWarning} />;
  const toolbarSlot = <>
      <NodeDescriptionToolbar description={data.description} errorMessage={executionStatus === "failure" ? nodeState?.error : undefined} defaultDescription={isX402Endpoint ? x402Info?.description : definition?.description} isVisible={!multipleNodesSelected && (showDescriptionToolbar || isCompactView && canShowSelectedToolbar) && !hasValidationErrors && !hasBalanceWarning} nodeName={data.name} nodeId={id} onInspect={hasActiveRun && executionStatus !== "idle" ? () => nodeExecutionDetailsRef.current?.openInspectModal() : undefined} onComment={data.onAddComment ? () => data.onAddComment?.(id) : undefined} onDuplicate={handleDuplicate} onDelete={handleDelete} onHoverChange={setToolbarHovered} nodeRef={nodeRootRef} tokens={descriptionTokens} payload={data.payload} formatHints={descriptionFormatHints} />
      <NodeValidationErrorToolbar errors={validationErrors} isVisible={hasValidationErrors && (isHovered || !!selected)} nodeName={data.name} nodeId={id} nodeRef={nodeRootRef} onDeselectNode={data.onDeselectNode} />
      <NodeGasWarningToolbar gasStatus={gasStatus} isVisible={hasBalanceWarning && !hasValidationErrors && (isHovered || !!selected)} nodeRef={nodeRootRef} />
    </>;
  const bannerSlot = <>
      {effectiveScheduledAt && scheduledRelativeTime && !showOutputPreview && <ScheduledBanner scheduledRelativeTime={scheduledRelativeTime} />}
      {needsDepositWallet && !walletsLoading && <PolymarketBanner hasDepositWallet={hasDepositWallet} hasPusd={isPolymarketAction ? polymarketHasPusd : true} onSetupWallet={() => setShowPolymarketSetupModal(true)} onFundWallet={polymarketFundingRecipient ? () => setShowPolymarketFundingModal(true) : undefined} />}
    </>;
  const bodySlot = isNotificationNode ? notificationMessage ? <div className="dark:bg-ds-neutral-800/50 rounded-md bg-zinc-50 px-3 py-2">
        <p className="text-ds-text-subtle dark:text-ds-text-disabled line-clamp-3 text-xs">{notificationMessage}</p>
      </div> : null : <NodeBodyContent showOutputPreview={!!showOutputPreview} nodeState={nodeState} executionStatus={executionStatus} isSendErc20Token={isSendErc20Token} payloadChainId={payloadChainId} tokenInfo={tokenInfo} payload={data.payload} chainInfoMap={chainInfoMap} recipientAddress={recipientAddress} recipientProfile={recipientProfile} getNodeName={getNodeName} isDexSwap={isDexSwap} tokenInInfo={tokenInInfo} tokenOutInfo={tokenOutInfo} isRelaySwap={isRelaySwap} srcChainId={srcChainId} dstChainId={dstChainId} isMathOperation={isMathOperation} isTriggerWorkflow={isTriggerWorkflow} targetWorkflowId={targetWorkflowId} targetWorkflow={targetWorkflow} configuredValues={configuredValues} hasConfiguredValues={hasConfiguredValues} isX402Endpoint={isX402Endpoint} x402Info={x402Info} dataType={data.type} slackChannelsMap={slackChannelsMap} isSlackConnector={isSlackConnector} telegramChatsMap={telegramChatsMap} quickBooksAccountsMap={quickBooksAccountsMap} inputAmountDecimals={inputAmountDecimals} resultDecimals={resultDecimals} vaultShareDecimals={vaultShareDecimals} resultSchema={definition?.resultSchema as Record<string, unknown> | undefined} payloadSchema={definition?.payloadSchema as Record<string, unknown> | undefined} />;
  const executionDetailsSlot = hasActiveRun && executionStatus !== "idle" && executionStatus !== "running" ? <NodeExecutionDetails ref={nodeExecutionDetailsRef} nodeState={nodeState} nodeInfo={nodeInfo} onFixWithAI={handleFixWithAI} workflowId={currentWorkflowId} runId={runId} /> : null;
  return <ContextMenu>
      <ContextMenuTrigger asChild>
        <ActionNodeShell id={id} dataApiNodeId={executionNodeId} name={displayName} subtitle={subtitleNode} iconUrl={iconVisual.logoUrl} connectorIconUrl={resolvedConnector?.logoUrl} connectorType={resolvedConnector?.type} iconId={data.type || "action"} iconSubject={iconSubject} iconContext={iconContext} iconAlt={iconAlt} status={hasActiveRun ? executionStatus : "idle"} selected={selected} needsConnector={needsConnector && !data.isTemplateView} hasBalanceWarning={hasBalanceWarning && !data.isTemplateView} hasValidationErrors={hasValidationErrors} isStreamingActive={isStreamingActive} showAIHighlight={showAIHighlight} showRotatingBorder={showRotatingBorder} showHoverBorder={showHoverBorder} streamingClass={streamingClass} hideSourceHandle={data.hideSourceHandle} horizontalHandles={data.horizontalHandles} compact={isCompactView} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} nodeRef={nodeRootRef} toolbarSlot={toolbarSlot} selectionIndicatorSlot={<NodeSelectionIndicator nodeId={id} />} bannerSlot={bannerSlot} bodySlot={bodySlot} executionDetailsSlot={executionDetailsSlot} commentsBadgeSlot={data.nodeComments ? <NodeCommentsBadge nodeComments={data.nodeComments} onAddComment={data.onAddNodeComment} /> : null} />
      </ContextMenuTrigger>
      <ActionNodeContextMenuContent id={id} onInfoOpen={() => setInfoModalOpen(true)} onDuplicate={handleDuplicate} onAddComment={data.onAddComment} onDelete={handleDelete} />

      <ActionNodeModals definition={definition ?? undefined} nodePayload={data.payload as Record<string, unknown> | undefined} errorModalOpen={errorModalOpen} onErrorModalChange={setErrorModalOpen} nodeInfo={nodeInfo} nodeState={nodeState} onFixWithAI={handleFixWithAI} onFieldUpdate={handleFieldUpdate} workflowId={currentWorkflowId} runId={runId} onRollback={data.onRollback} showDeleteConfirm={showDeleteConfirm} onDeleteConfirmChange={setShowDeleteConfirm} onConfirmDelete={confirmDelete} infoModalOpen={infoModalOpen} onInfoModalChange={setInfoModalOpen} modalDefinition={modalDefinition || null} />
      {needsDepositWallet && polymarketFundingRecipient && <PolymarketFundingModal open={showPolymarketFundingModal} onOpenChange={setShowPolymarketFundingModal} recipientAddress={polymarketFundingRecipient} wallets={polymarketOrgWallets} onFundingCompleted={() => setShowPolymarketFundingModal(false)} />}
      {needsDepositWallet && <PolymarketDepositWalletSetupModal open={showPolymarketSetupModal} onOpenChange={setShowPolymarketSetupModal} wallets={polymarketOrgWallets} onSetupCompleted={() => setShowPolymarketSetupModal(false)} />}
    </ContextMenu>;
}

// Custom comparator for React.memo - shallow compare primitives, deep compare objects
function arePropsEqual(prevProps: ActionNodeProps, nextProps: ActionNodeProps): boolean {
  if (prevProps.id !== nextProps.id || prevProps.selected !== nextProps.selected) {
    return false;
  }
  const prevData = prevProps.data;
  const nextData = nextProps.data;
  if (prevData === nextData) return true;
  if (!prevData || !nextData) return false;
  if (prevProps.data.isStreamingNew !== nextProps.data.isStreamingNew || prevProps.data.isStreamingEdit !== nextProps.data.isStreamingEdit || prevProps.data.isStreamingActive !== nextProps.data.isStreamingActive) {
    return false;
  }
  return prevData.name === nextData.name && prevData.type === nextData.type && prevData.description === nextData.description && prevData.platform === nextData.platform && prevData.connector === nextData.connector && prevData.isTemplateView === nextData.isTemplateView && isEqual(prevData.payload, nextData.payload) && isEqual(prevData.nodeComments, nextData.nodeComments);
}

// React.memo prevents re-renders when props haven't changed
// This is important for React Flow performance with many nodes
const ActionNode = React.memo(ActionNodeComponent, arePropsEqual);
export default ActionNode;